import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db78gfun6nb9f68e9arg";

const USE_RULES = [
  "本站内容仅用于产品演示与浏览体验，不代表任何商业发行行为。",
  "请尊重作品版权，支持正版渠道观看与购买相关周边。",
  "站内收藏、搜索等数据仅保存在您的浏览器本地，不会上传。",
  "如发现内容有误或涉及侵权，欢迎通过下方留言与我们联系。",
];

const USER_GUIDE = [
  "使用顶部搜索框，输入作品名称、题材或地区即可快速查找。",
  "在分类页可按照地区、年份与排序方式筛选心仪的动漫。",
  "进入详情页可以查看剧情简介、播放列表与相关推荐。",
  "点击「收藏追番」即可把喜欢的作品加入本地收藏夹。",
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || !parsed || parsed.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试。");
    }
  };

  return (
    <Layout>
      <Seo
        title="关于我们 - 樱花动漫"
        description="了解樱花动漫的平台定位、版权声明与内容来源说明，查看联系方式、使用须知与用户守则，共同营造健康合规的动漫观看环境。"
        keywords="樱花动漫,关于我们,版权声明,内容来源,用户守则"
        path="/about"
      />

      <div className="w-full px-4 pt-8 md:px-6 lg:px-10">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">关于我们</span>
        </nav>

        {/* 品牌介绍 */}
        <section className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-secondary-50">
          <div className="grid grid-cols-1 gap-6 p-6 md:p-10 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-flower-fill"></i>
                关于樱花动漫
              </span>
              <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">
                让每一次追番都安心而愉悦
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                樱花动漫是一个专注动漫在线观看的内容平台，内容覆盖热血、恋爱、科幻、经典等多元题材，既有正在连载的热门新作，也有历久弥新的经典动画。我们坚持正版合规的运营理念，致力于为不同年龄段的动漫爱好者提供清晰、顺畅、友好的浏览与观看体验。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                平台提供关键词搜索、分类筛选、收藏追番与相关内容推荐等功能，帮助您更快发现心动的作品。
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:col-span-1">
              <div className="rounded-lg bg-background-50 p-5 text-center">
                <div className="text-2xl font-bold text-primary-600">16+</div>
                <p className="mt-1 text-xs text-foreground-500">精选动漫作品</p>
              </div>
              <div className="rounded-lg bg-background-50 p-5 text-center">
                <div className="text-2xl font-bold text-accent-600">8</div>
                <p className="mt-1 text-xs text-foreground-500">热门题材分类</p>
              </div>
              <div className="rounded-lg bg-background-50 p-5 text-center">
                <div className="text-2xl font-bold text-secondary-700">4</div>
                <p className="mt-1 text-xs text-foreground-500">覆盖地区</p>
              </div>
              <div className="rounded-lg bg-background-50 p-5 text-center">
                <div className="text-2xl font-bold text-primary-600">100%</div>
                <p className="mt-1 text-xs text-foreground-500">本地收藏隐私</p>
              </div>
            </div>
          </div>
        </section>

        {/* 版权与内容来源 */}
        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-copyright-line text-primary-500"></i>
              版权声明
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站展示的动漫名称、简介与封面图均为演示用途的示例内容，相关作品版权归原著作权人及发行方所有。本站不提供任何视频存储、转码或播放服务，也不对第三方内容主张任何权益。若相关内容涉及版权问题，请通过下方联系方式与我们取得联系，我们会第一时间处理。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-database-2-line text-accent-500"></i>
              内容来源说明
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              演示站点中的作品信息由平台编辑整理后以结构化数据形式呈现，涵盖作品标题、题材标签、地区、年份、集数与更新进度等字段，便于后续接入正规内容库。所有页面文案均自然贴合动漫平台场景，不包含占位文字。
            </p>
          </div>
        </section>

        {/* 使用须知 & 用户守则 */}
        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-book-open-line text-primary-500"></i>
              使用须知
            </h2>
            <ul className="mt-4 space-y-3">
              {USE_RULES.map((rule) => (
                <li key={rule} className="flex gap-2.5 text-sm leading-relaxed text-foreground-600">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-500"></i>
                  {rule}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-user-heart-line text-accent-500"></i>
              用户守则
            </h2>
            <ul className="mt-4 space-y-3">
              {USER_GUIDE.map((rule) => (
                <li key={rule} className="flex gap-2.5 text-sm leading-relaxed text-foreground-600">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base text-accent-500"></i>
                  {rule}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 联系方式 + 留言 */}
        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="rounded-lg bg-secondary-50 p-6 lg:col-span-2">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-customer-service-2-line text-secondary-700"></i>
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              任何建议、合作或版权事宜，都欢迎与我们沟通。
            </p>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50 text-lg text-primary-600">
                  <i className="ri-mail-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">联系邮箱</p>
                  <p className="font-medium text-foreground-800">support@sakura-anime.example.com</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50 text-lg text-accent-600">
                  <i className="ri-time-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">服务时间</p>
                  <p className="font-medium text-foreground-800">周一至周日 09:00 - 21:00</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50 text-lg text-secondary-700">
                  <i className="ri-map-pin-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">办公地址</p>
                  <p className="font-medium text-foreground-800">示例市樱花区动漫大道 88 号</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-6 lg:col-span-3">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-chat-3-line text-primary-500"></i>
              给我们留言
            </h2>
            <p className="mt-2 text-sm text-foreground-500">留下你的想法，我们会认真阅读每一条建议。</p>

            <form id="about-contact-form" data-readdy-form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-sm text-foreground-700">昵称</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="你的昵称"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-sm text-foreground-700">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm text-foreground-700">留言内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  required
                  placeholder="请输入你的建议或问题（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                className="contact-extra-note"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                >
                  <i className="ri-send-plane-line"></i>
                  {status === "submitting" ? "提交中…" : "提交留言"}
                </button>
                {status === "success" ? (
                  <span className="flex items-center gap-1.5 text-sm text-primary-600">
                    <i className="ri-checkbox-circle-line"></i>
                    留言已提交，感谢你的反馈！
                  </span>
                ) : null}
                {status === "error" ? (
                  <span className="flex items-center gap-1.5 text-sm text-accent-700">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </span>
                ) : null}
              </div>
            </form>
          </div>
        </section>
      </div>
    </Layout>
  );
}